/* eslint-disable no-unused-vars */
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useEvent } from 'react-use'
import gsap from 'gsap'
import { SimplexNoise } from 'three-stdlib'

import { isCorporate } from '@/constants'

import { FluidProps } from '@/types'

export type ImageFluidProps = {
  children: (
    ref: RefObject<HTMLImageElement | HTMLVideoElement>
  ) => React.ReactNode
} & Partial<FluidProps> & {
    square?: boolean
    gradient?: boolean
    isVisible?: boolean
  }

const ImageFluid = ({
  children,
  radius = 20,
  segments = 40,
  amplitude = 10,
  period = 0.001,
  speed = 0.005,
  square = false,
  gradient = false,
  isVisible = false
}: ImageFluidProps) => {
  const root = useRef<HTMLImageElement | HTMLVideoElement>(null!)
  const canvas = useRef<HTMLCanvasElement>(null!)
  const context = useRef<CanvasRenderingContext2D | null>(null)
  const variation = useRef(0)
  const [perlin] = useState(() => new SimplexNoise())
  const [points] = useState<Record<string, { x: number; y: number }[]>>(() => ({
    top: [],
    bottom: []
  }))

  const setup = useCallback(() => {
    context.current = canvas.current?.getContext('2d')
    gsap.set(root.current, { opacity: 0 })
  }, [])

  const drawClipPath = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const width = canvas.current.width
      const height = canvas.current.height

      const offsetTop = amplitude
      const offsetBot = height - amplitude
      const segment = (width - radius * 2) / segments

      points.top = []
      points.bottom = []
      for (let x = 0, u = 0; x <= segments; x++, u += segment) {
        const point =
          perlin.noise(u * period - variation.current, u * period) * amplitude
        points.top.push({
          x: radius + x * segment,
          y: point + offsetTop
        })
        points.bottom.push({
          x: radius + (width - radius * 2 - x * segment),
          y: point + offsetBot
        })
      }

      const firstTopPoint = points.top[0]
      const lastTopPoint = points.top[points.top.length - 1]

      const firstBottomPoint = points.bottom[0]
      const lastBottomPoint = points.bottom[points.bottom.length - 1]

      ctx.beginPath()

      for (let i = 0; i < points.top.length - 1; i++) {
        ctx.lineTo(points.top[i].x, points.top[i].y)
      }

      ctx.lineTo(width - radius, lastTopPoint.y)
      ctx.quadraticCurveTo(
        width,
        lastTopPoint.y,
        width,
        lastTopPoint.y + radius
      )

      ctx.lineTo(width, firstBottomPoint.y - radius)
      ctx.quadraticCurveTo(
        width,
        firstBottomPoint.y,
        width - radius,
        firstBottomPoint.y
      )

      for (let i = 0; i < points.bottom.length - 1; i++) {
        ctx.lineTo(points.bottom[i].x, points.bottom[i].y)
      }

      ctx.lineTo(radius, lastBottomPoint.y)
      ctx.quadraticCurveTo(0, lastBottomPoint.y, 0, lastBottomPoint.y - radius)

      ctx.lineTo(0, firstTopPoint.y + radius)
      ctx.quadraticCurveTo(0, firstTopPoint.y, radius, firstTopPoint.y)

      ctx.closePath()
    },
    [perlin, points, radius, segments, amplitude, period]
  )

  const drawSource = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const width = canvas.current.width
      const source = root.current
      const height = canvas.current.height
      const isImage = source instanceof HTMLImageElement
      const isVideo = source instanceof HTMLVideoElement

      const sourceWidth = isImage
        ? source.naturalWidth
        : isVideo
          ? source.videoWidth
          : square
            ? 500
            : 0
      const sourceHeight = isImage
        ? source.naturalHeight
        : isVideo
          ? source.videoHeight
          : square
            ? 500
            : 0
      const sourceRatio = sourceWidth / sourceHeight
      const canvasRatio = width / height

      const coverWidth =
        canvasRatio > sourceRatio ? width : height * sourceRatio
      const coverHeight =
        canvasRatio > sourceRatio ? width / sourceRatio : height
      const coverX = (width - coverWidth) / 2
      const coverY = (height - coverHeight) / 2

      ctx.drawImage(source, coverX, coverY, coverWidth, coverHeight)

      if (gradient) {
        const gradient = ctx.createLinearGradient(0, 0, 0, height)
        gradient.addColorStop(0, 'rgba(0,0,0,0)')
        gradient.addColorStop(1, 'rgba(0,0,0,80)')
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, width, height)
      }
    },
    [gradient, square]
  )

  const tick = useCallback(() => {
    if (!context.current) return

    const ctx = context.current
    const width = canvas.current.width
    const height = canvas.current.height

    ctx.clearRect(0, 0, width, height)

    ctx.save()

    drawClipPath(ctx)

    ctx.clip()

    drawSource(ctx)

    ctx.restore()

    variation.current -= speed
  }, [speed, drawClipPath, drawSource])

  const addListeners = useCallback(() => {
    if (root.current instanceof HTMLImageElement) {
      root.current.addEventListener('load', setup)
    }

    if (root.current instanceof HTMLVideoElement) {
      root.current.addEventListener('loadeddata', setup)
      root.current.load()
    }
  }, [setup])

  const removeListeners = useCallback(() => {
    root.current?.removeEventListener('load', setup)
    root.current?.removeEventListener('loadeddata', setup)
  }, [setup])

  const resize = () => {
    if (!canvas.current) return

    const { width, height } = root.current.getBoundingClientRect()
    const dpr = 1.5
    canvas.current.width = width * dpr
    canvas.current.height = height * dpr
  }

  useEffect(() => resize(), [])
  useEvent('resize', resize)

  useEffect(() => {
    if (!canvas.current) return

    addListeners()
    gsap.ticker.add(tick)

    return () => {
      removeListeners()
      gsap.ticker.remove(tick)
    }
  }, [tick, addListeners, removeListeners])

  return (
    <>
      {isCorporate || isVisible ? (
        <canvas ref={canvas} className="h-full w-full" />
      ) : null}
      {children(root)}
    </>
  )
}

export default ImageFluid
